import { ATTR_PREFIX } from './contract';
import { effectiveMotion, type Prefs } from './prefs';
import { setTextAlign } from './text-align';
import { setTextSize } from './text-scale';

export interface Environment {
  /** The device has "reduce motion" turned on. */
  deviceReducesMotion: boolean;
}

/**
 * The page-level half of the prefs: the `data-ea11y-*` attributes that
 * css/main.css keys off, plus the parts CSS cannot do (text size, aligning
 * text that sits straight in a <div>, pausing media). The reading guide and
 * big cursor inside the panel belong to the widget, which owns that DOM.
 *
 * Idempotent. The inline <head> snippet (AccessibilityWidgetEmbed.java) sets
 * the same attributes before first paint; this re-applies them and adds the
 * rest.
 */
const FLAG_KEYS = ['spacing', 'links', 'images', 'cursor'] as const;

export function applyPrefs(prefs: Prefs, env: Environment): void {
  const root = document.documentElement;
  FLAG_KEYS.forEach((key) => setFlag(root, key, prefs[key]));

  const motion = effectiveMotion(prefs, env.deviceReducesMotion);
  setFlag(root, 'motion', motion);

  setValue(root, 'contrast', prefs.contrast);
  setValue(root, 'saturation', prefs.saturation);
  setValue(root, 'font', prefs.font);
  setValue(root, 'align', prefs.align);

  setTextSize(prefs.textSize);
  setTextAlign(prefs.align);
  setMediaPaused(motion);
}

/** `data-ea11y-<key>="<value>"`, or no attribute for the default `none`. */
function setValue(root: HTMLElement, key: string, value: string): void {
  if (value === 'none') {
    root.removeAttribute(`${ATTR_PREFIX}${key}`);
  } else {
    root.setAttribute(`${ATTR_PREFIX}${key}`, value);
  }
}

function setFlag(root: HTMLElement, key: string, on: boolean): void {
  if (on) {
    root.setAttribute(`${ATTR_PREFIX}${key}`, '');
  } else {
    root.removeAttribute(`${ATTR_PREFIX}${key}`);
  }
}

// ---- media -----------------------------------------------------------------

type Marquee = HTMLElement & { stop?: () => void; start?: () => void };

let mediaPaused = false;
const pausedByUs = new Set<HTMLMediaElement>();
const stoppedByUs = new Set<Marquee>();
let mediaObserver: MutationObserver | null = null;

/**
 * Pause animations also pauses media that is playing and stops <marquee>.
 * Turning it off resumes only what this code paused, so a video the visitor
 * paused themselves stays paused.
 */
function setMediaPaused(on: boolean): void {
  if (on === mediaPaused) return;
  mediaPaused = on;

  if (on) {
    pauseIn(document);
    if (typeof MutationObserver !== 'undefined' && document.body) {
      // Autoplaying media and marquees added after load (carousels, embeds).
      mediaObserver = new MutationObserver((records) => {
        records.forEach((record) =>
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) pauseIn(node as Element);
          }),
        );
      });
      mediaObserver.observe(document.body, { childList: true, subtree: true });
    }
    return;
  }

  if (mediaObserver) mediaObserver.disconnect();
  mediaObserver = null;
  pausedByUs.forEach((media) => {
    if (media.isConnected && media.paused) {
      const played = media.play();
      if (played && typeof played.catch === 'function')
        played.catch(() => undefined);
    }
  });
  pausedByUs.clear();
  stoppedByUs.forEach((marquee) => {
    if (marquee.isConnected && typeof marquee.start === 'function')
      marquee.start();
  });
  stoppedByUs.clear();
}

function pauseIn(root: Document | Element): void {
  const list = root.querySelectorAll('video, audio, marquee');
  const nodes: Element[] = [];
  if (root instanceof Element && root.matches('video, audio, marquee'))
    nodes.push(root);
  for (let i = 0; i < list.length; i++) nodes.push(list[i]);

  nodes.forEach((node) => {
    if (node instanceof HTMLMediaElement) {
      // Playing now, or about to autoplay once it has loaded.
      if (!node.paused || node.autoplay) {
        node.pause();
        node.autoplay = false;
        pausedByUs.add(node);
      }
    } else {
      const marquee = node as Marquee;
      if (typeof marquee.stop === 'function') {
        marquee.stop();
        stoppedByUs.add(marquee);
      }
    }
  });
}
